import * as React from "react";
import { cleanedErrors, dateStringError, validPhoneNumber, emailErrors } from "@/utils/Functions";
import { DeceasedErrors, DeceasedRecord } from "@/types/Person";
import { DateInput } from "@/components/atoms/DateInput";
import { TextInput } from "@/components/atoms/TextInput";
import { NewTitleInput } from "@/components/atoms/TitleInput";
import { phoneNumberInfoBox, emailAddressInfoBox } from "@/components/atoms/infoBox";
import { PhoneNumberTextInput } from "@/components/atoms/PhoneNumberTextInput";
import { EmailAddressInput } from "@/components/atoms/EmailAddressInput";
import { FormField } from "@/components/molecules/FormField";
import { InfoBoxTrigger } from "@/components/molecules/InfoBoxTrigger";
import { useOnChange, useOnValueChange } from "@/utils/callbacks";
import { FormStack } from "@/components/molecules";
import {
  ExpressStoreKey,
  getExpressSectionSourceDataByKey,
  putExpressSectionDataByKey,
} from "./express-data";

export interface DeceasedFormHandle {
  hasErrors: () => boolean;
  state: () => DeceasedRecord;
  errors: () => DeceasedErrors;
}
export interface IExpressDeceasedForm {
  // Define additional props here if needed.
}
export interface IDeceasedForm {
  readonly record: DeceasedRecord;
  readonly errors: DeceasedErrors;
  readonly update: React.Dispatch<React.SetStateAction<DeceasedRecord>>;
}

const CURRENT_SECTION_STORAGE_KEY = ExpressStoreKey.Deceased;
// const FORM_STEP_CURRENT_SECTION = ExpressFormMenuKey.Account;

export const ExpressDeceasedForm = React.forwardRef<DeceasedFormHandle, IExpressDeceasedForm>(
  function ExpressDeceasedForm(props, ref): React.ReactElement {
    const [deceased, setDeceased] = React.useState<DeceasedRecord>(null);
    const [deceasedErrors, setDeceasedErrors] = React.useState<DeceasedErrors>(undefined);

    React.useEffect(
      () => setDeceased(getExpressSectionSourceDataByKey(CURRENT_SECTION_STORAGE_KEY)),
      []
    );
    React.useImperativeHandle(
      ref,
      () => ({
        hasErrors: () => {
          const e: DeceasedErrors = errorsOfDeceased(deceased);
          setDeceasedErrors(e);
          if (!e) {
            putExpressSectionDataByKey(CURRENT_SECTION_STORAGE_KEY, {
              source: deceased,
              target: deceased,
            });
          }
          return !!e;
        },
        state: () => deceased,
        errors: () => errorsOfDeceased(deceased),
      }),
      [deceased]
    );

    return <DeceasedForm record={deceased} errors={deceasedErrors} update={setDeceased} />;
  }
);

function DeceasedForm({ record, errors, update }: IDeceasedForm): React.ReactElement {
  const onTitleChange = useOnValueChange(update, "title");
  const onDateOfBirthChange = useOnValueChange(update, "dateOfBirth");
  const onDateOfDeathChange = useOnValueChange(update, "dateOfDeath");
  const onChange = useOnChange(update);

  return (
    <FormStack>
      <FormField halfWidthByItself label="Their title">
        <NewTitleInput
          name="title"
          onValueChange={onTitleChange}
          value={record?.title ?? ""}
          error={errors?.title}
        />
      </FormField>

      <FormField halfWidth label="Their first name">
        <TextInput
          name="firstName"
          value={record?.firstName ?? ""}
          onChange={onChange}
          error={errors?.firstName}
        />
      </FormField>

      <FormField halfWidth label="Their last name">
        <TextInput
          name="lastName"
          value={record?.lastName ?? ""}
          onChange={onChange}
          error={errors?.lastName}
        />
      </FormField>

      <FormField halfWidth label="Date of birth">
        <DateInput
          value={record?.dateOfBirth ?? ""}
          onValueChange={onDateOfBirthChange}
          error={errors?.dateOfBirth}
          pastOnly
        />
      </FormField>

      <FormField halfWidth label="Date of death">
        <DateInput
          value={record?.dateOfDeath ?? ""}
          onValueChange={onDateOfDeathChange}
          error={errors?.dateOfDeath}
          pastOnly
        />
      </FormField>

      <FormField halfWidthByItself label="Postcode">
        <TextInput
          name="postcode"
          value={record?.postcode ?? ""}
          onChange={onChange}
          error={errors?.postcode}
        />
      </FormField>

      <FormField
        halfWidth
        label={
          <span>
            Phone <InfoBoxTrigger white width="448px" content={phoneNumberInfoBox()} />
          </span>
        }
      >
        <PhoneNumberTextInput
          name="contactNumber"
          value={record?.contactNumber ?? ""}
          onChange={onChange}
          error={errors?.contactNumber}
        />
      </FormField>

      <FormField
        halfWidth
        label={
          <span>
            Email <InfoBoxTrigger white width="448px" content={emailAddressInfoBox()} />
          </span>
        }
      >
        <EmailAddressInput
          name="email"
          value={record?.email ?? ""}
          onChange={onChange}
          error={errors?.email}
        />
      </FormField>
    </FormStack>
  );
}

export function errorsOfDeceased(record: DeceasedRecord): DeceasedErrors {
  return cleanedErrors({
    title: record?.title ? undefined : "required",
    firstName: record?.firstName ? undefined : "required",
    lastName: record?.lastName ? undefined : "required",
    dateOfBirth: dateStringError(record?.dateOfBirth),
    dateOfDeath: dateStringError(record?.dateOfDeath),
    postcode: record?.postcode ? undefined : "required",
    email: emailErrors(record?.email),
    contactNumber:
      !record?.contactNumber || validPhoneNumber(record?.contactNumber)
        ? undefined
        : "valid phone number required",
  });
}
